import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconCalendarPlus, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import CopyButton from '../components/ui/CopyButton';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import FormField from '../components/ui/FormField';
import CustomerPicker from '../components/ui/CustomerPicker';
import ErrorMessage from '../components/ui/ErrorMessage';
import { formatDateTime } from '../utils/formatDate';
import { usePaginatedList } from '../hooks/usePaginatedList';
import './CrudPage.scss';

function SecretCell({ value }) {
    if (!value) return '—';
    return (
        <div className="cell-secret">
            <span title={value}>{value}</span>
            <CopyButton value={value} />
        </div>
    );
}

function emptyCreateForm() {
    return {
        server_id: '',
        devices: '2',
        days: '31',
    };
}

function formatServerLabel(server) {
    const host = server.domain || server.ip || '—';
    return `#${server.id} ${host}`;
}

export default function SubscriptionsPage() {
    const fetchList = useCallback(
        (params) => api.listResource('subscriptions', params),
        [],
    );
    const {
        items, total, offset, limit, loading, error, setOffset, reload,
    } = usePaginatedList(fetchList);

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [extendTarget, setExtendTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [createForm, setCreateForm] = useState(emptyCreateForm());
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [servers, setServers] = useState([]);
    const [serversLoading, setServersLoading] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [extendDays, setExtendDays] = useState('31');
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const loadServers = useCallback(async () => {
        if (servers.length > 0) return;

        setServersLoading(true);
        try {
            const data = await api.listResource('servers', { limit: 200 });
            setServers(data.items || []);
        } catch (err) {
            setFormError(err.message || 'Не удалось загрузить серверы');
        } finally {
            setServersLoading(false);
        }
    }, [servers.length]);

    useEffect(() => {
        if (createOpen) {
            loadServers();
        }
    }, [createOpen, loadServers]);

    const openCreate = () => {
        setCreateForm(emptyCreateForm());
        setSelectedCustomer(null);
        setFormError('');
        setCreateOpen(true);
    };

    const serverOptions = useMemo(() => [
        { value: '', label: 'Выберите сервер…' },
        ...servers.map((server) => ({
            value: String(server.id),
            label: formatServerLabel(server),
        })),
    ], [servers]);

    const openEdit = (row) => {
        setEditTarget(row);
        setEditForm({
            url: row.url || '',
            xui_email: row.xui_email || '',
            xui_sub_id: row.xui_sub_id || '',
            devices: String(row.devices ?? ''),
            vpn_key: row.vpn_key || '',
            end_at: row.end_at ? row.end_at.slice(0, 16) : '',
        });
        setFormError('');
    };

    const handleCreate = async () => {
        if (!selectedCustomer?.id) {
            setFormError('Выберите клиента');
            return;
        }

        if (!createForm.server_id) {
            setFormError('Выберите сервер');
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            await api.createResource('subscriptions', {
                customer_id: selectedCustomer.id,
                server_id: Number(createForm.server_id),
                devices: Number(createForm.devices),
                days: Number(createForm.days),
            });
            setCreateOpen(false);
            setCreateForm(emptyCreateForm());
            setSelectedCustomer(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка создания');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async () => {
        if (!editTarget) return;
        setSaving(true);
        setFormError('');
        try {
            const patch = {};
            Object.entries(editForm).forEach(([key, value]) => {
                if (key === 'devices') {
                    const num = Number(value);
                    if (num !== editTarget.devices) patch.devices = num;
                    return;
                }
                if (key === 'end_at') {
                    const iso = value ? new Date(value).toISOString() : null;
                    if (iso !== editTarget.end_at) patch.end_at = iso;
                    return;
                }
                if (value !== (editTarget[key] ?? '')) patch[key] = value || null;
            });
            await api.updateResource('subscriptions', editTarget.id, patch);
            setEditTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleExtend = async () => {
        if (!extendTarget) return;
        setSaving(true);
        setFormError('');
        try {
            await api.extendSubscription(extendTarget.id, Number(extendDays));
            setExtendTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка продления');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteResource('subscriptions', deleteTarget.id);
            setDeleteTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка удаления');
        } finally {
            setDeleting(false);
        }
    };

    const columns = useMemo(() => [
        { key: 'id', title: '№', width: 60 },
        { key: 'customer_id', title: 'ID', width: 90 },
        { key: 'customer_login', title: 'Логин' },
        { key: 'server_id', title: 'Сервер' },
        { key: 'devices', title: 'Устройств' },
        {
            key: 'end_at',
            title: 'Окончание',
            render: (v) => formatDateTime(v),
        },
        {
            key: 'url',
            title: 'URL',
            render: (v) => <SecretCell value={v} />,
        },
        {
            key: 'actions',
            title: '',
            render: (_, row) => (
                <div className="cell-actions">
                    <button type="button" className="btn btn--outline btn--sm" title="Продлить" onClick={() => { setExtendTarget(row); setExtendDays('31'); setFormError(''); }}>
                        <IconCalendarPlus size={16} />
                    </button>
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => openEdit(row)}>
                        <IconEdit size={16} />
                    </button>
                    <button type="button" className="btn btn--danger btn--sm" onClick={() => setDeleteTarget(row)}>
                        <IconTrash size={16} />
                    </button>
                </div>
            ),
        },
    ], []);

    return (
        <div className="crud-page">
            <PageHeader
                title="Подписки"
                subtitle="VPN-подписки клиентов"
                actions={(
                    <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                        <IconPlus size={16} />
                        Создать
                    </button>
                )}
            />

            <ErrorMessage message={error} onRetry={reload} />

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                pagination={{ total, limit, offset, onPageChange: setOffset }}
            />

            <Modal
                open={createOpen}
                title="Новая подписка"
                onClose={() => setCreateOpen(false)}
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setCreateOpen(false)}>Отмена</button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleCreate} disabled={saving}>
                            {saving ? 'Создание...' : 'Создать'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <div className="form-grid">
                    <CustomerPicker
                        label="Клиент"
                        value={selectedCustomer}
                        onChange={setSelectedCustomer}
                    />
                    <FormField
                        label="Сервер"
                        value={createForm.server_id}
                        onChange={(e) => setCreateForm((p) => ({ ...p, server_id: e.target.value }))}
                        options={serverOptions}
                        disabled={serversLoading}
                    />
                    <FormField label="Устройств" type="number" value={createForm.devices} onChange={(e) => setCreateForm((p) => ({ ...p, devices: e.target.value }))} />
                    <FormField label="Дней" type="number" value={createForm.days} onChange={(e) => setCreateForm((p) => ({ ...p, days: e.target.value }))} />
                </div>
            </Modal>

            <Modal
                open={Boolean(editTarget)}
                title={editTarget ? `Подписка #${editTarget.id}` : ''}
                onClose={() => setEditTarget(null)}
                wide
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditTarget(null)}>Отмена</button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleEdit} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <div className="form-grid">
                    <FormField label="URL" value={editForm.url} onChange={(e) => setEditForm((p) => ({ ...p, url: e.target.value }))} />
                    <FormField label="XUI email" value={editForm.xui_email} onChange={(e) => setEditForm((p) => ({ ...p, xui_email: e.target.value }))} />
                    <FormField label="XUI sub ID" value={editForm.xui_sub_id} onChange={(e) => setEditForm((p) => ({ ...p, xui_sub_id: e.target.value }))} />
                    <FormField label="Устройств" type="number" value={editForm.devices} onChange={(e) => setEditForm((p) => ({ ...p, devices: e.target.value }))} />
                    <FormField label="VPN ключ" value={editForm.vpn_key} onChange={(e) => setEditForm((p) => ({ ...p, vpn_key: e.target.value }))} />
                    <FormField label="Окончание" type="datetime-local" value={editForm.end_at} onChange={(e) => setEditForm((p) => ({ ...p, end_at: e.target.value }))} />
                </div>
            </Modal>

            <Modal
                open={Boolean(extendTarget)}
                title={`Продлить подписку #${extendTarget?.id || ''}`}
                onClose={() => setExtendTarget(null)}
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setExtendTarget(null)}>Отмена</button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleExtend} disabled={saving}>
                            {saving ? 'Продление...' : 'Продлить'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <FormField
                    label="Дней"
                    type="number"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    hint="Подписка будет продлена в XUI"
                />
            </Modal>

            <ConfirmModal
                open={Boolean(deleteTarget)}
                title="Удалить подписку?"
                message={`Подписка #${deleteTarget?.id} будет удалена безвозвратно с отзывом в XUI.`}
                confirmLabel="Удалить"
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}
